// Sanitizador único para el HTML de correo que el CRM pinta en su propio
// origen (auditoría 2026-08-14, CN-011).
//
// El problema que cierra: cuatro sitios hacían dangerouslySetInnerHTML sobre
// cuerpos de correo escritos por el inquilino —plantillas, envíos masivos,
// campañas de plataforma— y NO existía ningún sanitizador en el proyecto. La
// vía realista no era un atacante externo sino escalada dentro de la agencia:
// un SEAT con permiso de editar plantillas redacta un cuerpo, la dueña abre la
// vista previa, y el marcado corre en la sesión de la dueña.
//
// Hasta ahora lo único que lo frenaba era la CSP (sin 'unsafe-inline' en
// script-src, así que un onerror= no ejecuta y un <script> insertado por
// innerHTML tampoco). Eso es UNA sola capa, y para una app con PHI una sola
// capa es poco. Esto añade la segunda, en el sitio correcto.
//
// Criterio del filtro: se parte de los valores por defecto de DOMPurify, que
// ya quitan <script>, todos los manejadores on*, y las URL javascript:/data:
// peligrosas, pero DEJAN pasar el marcado de presentación que un correo
// necesita de verdad (tablas, imágenes, enlaces, atributo style). Se prefirió
// eso a una lista blanca cerrada a mano porque una lista corta rompería
// visiblemente la vista previa de plantillas que ya existen, y una regresión
// visual en la UI es un costo real.
//
// Encima de eso se prohíbe explícitamente lo que no tiene ningún sentido
// dentro de una previsualización y sí sirve para suplantar la interfaz:
//   - <style> y <link>: podrían re-estilar el CRM entero alrededor del
//     preview, que es justo el vector de suplantación de UI del reporte.
//   - <form>/<input>/<button>: un formulario falso pidiendo credenciales.
//   - <base>: reescribe el destino de cada enlace relativo de la página.
//   - <iframe>/<object>/<embed>: contenido de terceros dentro del origen.
//   - srcdoc/formaction/ping: vías conocidas para saltarse lo anterior.

import DOMPurify from 'dompurify';
// El criterio de que sobrevive a un PEGADO vive aparte y sin dependencias, para
// que las pruebas del backend (que corren en Node y no instalan lo de crm-app)
// puedan importarlo. Aqui esta el recorrido del DOM, que si necesita navegador.
import { etiquetasPermitidas, atributosPermitidos, estiloPermitido } from './htmlPegado.js';

const CONFIG = {
  FORBID_TAGS: [
    'style', 'link', 'base', 'meta',
    'form', 'input', 'button', 'textarea', 'select', 'option',
    'iframe', 'object', 'embed', 'applet',
  ],
  FORBID_ATTR: ['srcdoc', 'formaction', 'ping', 'target'],
  ALLOW_DATA_ATTR: false,
  // Las URL siguen pasando por el saneado propio de DOMPurify; data: se
  // permite solo en imágenes porque las firmas y los logos embebidos viajan
  // así (ver el historial de campañas: nunca data:image en el cuerpo, pero sí
  // en la firma).
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|cid):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

/**
 * Limpia HTML de correo para pintarlo dentro del CRM.
 * @param {string} html cuerpo tal como lo guardó el inquilino
 * @returns {string} HTML seguro para dangerouslySetInnerHTML
 */
export function sanitizeEmailHtml(html) {
  if (html == null) return '';
  return DOMPurify.sanitize(String(html), CONFIG);
}

/**
 * Igual que sanitizeEmailHtml pero devuelve el objeto que espera React, para
 * que en el sitio de uso se lea `dangerouslySetInnerHTML={safeHtml(x)}` y no
 * haya forma de olvidarse del saneado a medio camino.
 * @param {string} html
 */
export function safeHtml(html) {
  return { __html: sanitizeEmailHtml(html) };
}

/**
 * EL HTML QUE LLEGA PEGADO DE FUERA, RECORTADO A LO QUE EL EDITOR HONRA.
 *
 * ── NO ES EL MISMO TRABAJO QUE `sanitizeEmailHtml`, Y POR ESO NO ES LA MISMA
 *    FUNCION ────────────────────────────────────────────────────────────────
 *
 * Aquella filtra por SEGURIDAD y es permisiva a proposito (deja tablas,
 * imagenes, enlaces y el atributo `style` entero) porque cerrarla romperia
 * visiblemente plantillas que ya existen. Esta filtra por FIDELIDAD Y PESO: se
 * queda solo con lo que la barra de ESE editor puede producir y —en Firmas— lo
 * que el PDF firmado sabe dibujar. Reusar la permisiva no arreglaria nada: la
 * basura de Word es toda `<span style="...">`, que aquella conserva entera.
 *
 * Se aplica AL PEGAR y no al guardar. Es donde entra la basura, es donde la
 * persona esta mirando, y es lo unico que deja el valor guardado limpio sin
 * tener que tocar las plantillas que ya estan escritas.
 *
 * @param {string} html lo que traiga el portapapeles en `text/html`
 * @param {{sinImagen?: boolean, sinEnlace?: boolean, sinFuente?: boolean}} opciones
 *   las MISMAS banderas del editor: lo que se pega puede traer lo que sus
 *   botones pueden hacer, ni mas ni menos.
 * @returns {string} HTML listo para insertar en el cursor
 */
export function limpiarHtmlPegado(html, opciones = {}) {
  if (html == null) return '';
  const trozo = DOMPurify.sanitize(String(html), {
    ALLOWED_TAGS: etiquetasPermitidas(opciones),
    ALLOWED_ATTR: atributosPermitidos(opciones),
    ALLOW_DATA_ATTR: false,
    RETURN_DOM_FRAGMENT: true,
  });

  // El `style` se recorta propiedad a propiedad: DOMPurify decide si el
  // atributo pasa, no lo que lleva dentro, y lo que pesa esta dentro.
  for (const el of trozo.querySelectorAll('[style]')) {
    const limpio = estiloPermitido(el.getAttribute('style'), opciones);
    if (limpio) el.setAttribute('style', limpio);
    else el.removeAttribute('style');
  }

  // Un `<span>` al que no le quedo ningun atributo ya no dice nada y sigue
  // pesando. Word envuelve CADA palabra en uno (`<span lang=ES>`), asi que
  // deshacerlos es la mitad del ahorro. Se deshace de dentro hacia fuera para
  // que los anidados —tambien los suyos— caigan todos en una pasada.
  const spans = [...trozo.querySelectorAll('span:not([style])')].reverse();
  for (const el of spans) el.replaceWith(...el.childNodes);

  const caja = document.createElement('div');
  caja.appendChild(trozo);
  return caja.innerHTML;
}
